import { useState } from "react";
import { ChevronDown, ChevronRight, CheckCircle2, Loader2, Zap } from "lucide-react";
import { Card, Badge } from "@/components/ui/primitives";
import type { JourneyEventDTO } from "@/lib/api";
import type { PlaybackState } from "@/hooks/usePlayback";
import { cn } from "@/lib/utils";

type Grouped = Record<string, JourneyEventDTO[]>;

export function JourneyTimeline({
  events,
  playback,
}: {
  events: JourneyEventDTO[];
  playback?: PlaybackState;
}) {
  const visibleEvents = playback
    ? events.filter((e) => playback.visibleEventIds.has(e.id))
    : events;

  const grouped: Grouped = {};
  const agentOrder: string[] = [];
  for (const e of events) {
    if (!(e.agent_name in grouped)) {
      grouped[e.agent_name] = [];
      agentOrder.push(e.agent_name);
    }
  }
  for (const e of visibleEvents) {
    grouped[e.agent_name].push(e);
  }

  const visibleAgents = agentOrder.filter((name) => grouped[name].length > 0);
  const showSynthesiser = playback?.synthesiserActive === true;

  if (visibleAgents.length === 0 && !showSynthesiser) {
    if (playback?.isPlaying) {
      return (
        <Card className="flex items-center gap-3 p-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-warn" strokeWidth={2.2} />
          Agents spinning up…
        </Card>
      );
    }
    return (
      <Card className="p-4 text-sm text-muted-foreground">No agent activity recorded yet.</Card>
    );
  }

  return (
    <Card className="divide-y divide-border">
      {visibleAgents.map((name) => (
        <AgentGroup
          key={name}
          name={name}
          events={grouped[name]}
          forceOpen={playback?.isPlaying ?? false}
        />
      ))}
      {showSynthesiser && <SynthesiserRow />}
    </Card>
  );
}

function AgentGroup({
  name,
  events,
  forceOpen,
}: {
  name: string;
  events: JourneyEventDTO[];
  forceOpen: boolean;
}) {
  const [userOpen, setUserOpen] = useState(false);
  const open = forceOpen || userOpen;
  const last = events[events.length - 1];
  const totalMs = events.reduce((a, b) => a + b.duration_ms, 0);
  const isDone = last?.phase === "done" || last?.phase === "observation";
  return (
    <div>
      <button
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-muted/30 transition-colors"
        onClick={() => setUserOpen((v) => !v)}
      >
        <span className="text-muted-foreground">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        <span
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-full",
            isDone ? "bg-success/10 text-success" : "bg-warn/10 text-warn",
          )}
        >
          {isDone ? (
            <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.2} />
          ) : (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          )}
        </span>
        <span className="text-sm font-medium flex-1">{name}</span>
        <span className="text-xs text-muted-foreground">{(totalMs / 1000).toFixed(1)}s</span>
        <Badge variant="muted">{events.length} phases</Badge>
      </button>
      {open && (
        <ol className="space-y-3 border-t border-border bg-muted/10 px-5 py-4 pl-14">
          {events.map((e) => (
            <PhaseRow key={e.id} e={e} />
          ))}
        </ol>
      )}
    </div>
  );
}

function SynthesiserRow() {
  return (
    <div className="flex items-center gap-3 px-5 py-3.5 animate-in fade-in">
      <span className="text-muted-foreground">
        <ChevronRight className="h-4 w-4" />
      </span>
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent/10 text-accent">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      </span>
      <span className="text-sm font-medium flex-1">
        Analyst Agent
        <span className="ml-2 text-xs font-normal text-muted-foreground">synthesising findings…</span>
      </span>
    </div>
  );
}

function PhaseRow({ e }: { e: JourneyEventDTO }) {
  const phaseLabel: Record<string, string> = {
    plan: "Plan",
    reasoning: "Reasoning",
    action: "Action",
    observation: "Observation",
    done: "Done",
    error: "Error",
  };
  const phaseTone: Record<string, string> = {
    plan: "text-blue-500",
    reasoning: "text-accent",
    action: "text-brand",
    observation: "text-warn",
    done: "text-success",
    error: "text-destructive",
  };
  return (
    <li className="flex gap-3 text-sm animate-in fade-in slide-in-from-left-2 duration-300">
      <span className={cn("mt-1 flex-shrink-0", phaseTone[e.phase])}>
        <Zap className="h-3.5 w-3.5" strokeWidth={2.2} />
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn("text-[0.72rem] font-semibold uppercase tracking-wider", phaseTone[e.phase])}>
            {phaseLabel[e.phase] ?? e.phase}
          </span>
          <span className="text-[0.72rem] text-muted-foreground">{(e.duration_ms / 1000).toFixed(1)}s</span>
        </div>
        <p className="text-foreground/90">{e.message}</p>
      </div>
    </li>
  );
}
